import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Filter, Lock, Pencil, RefreshCw, Save, Search, Unlink, UserCheck, X } from 'lucide-react';
import type { CustomerManagerCustomerSummary, OpsUserRecord } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useAssignCustomerManager,
  useCustomerOwnershipCustomers,
  useRevokeCustomerManager,
} from '../api/customerOwnership.ts';
import { useOpsUsers } from '../api/opsUsers.ts';
import { usePricing } from '../api/pricing.ts';
import {
  ROLE_OPS_ADMIN,
  ROLE_OPS_CUSTOMER_MANAGER,
  hasAnyOpsRole,
} from '../auth/opsRoles.ts';
import { BrandChip } from '../components/BrandChip.tsx';
import { CustomerCell, CustomerCompanyLine } from '../components/identity/CustomerCell.tsx';
import { BrandScopeBanner } from '../components/BrandScopeBanner.tsx';
import { formatLocalTimestamp, formatUsdMicrosOrCentsAsCnyDisplayBalance } from '../components/formatters.ts';
import { Modal } from '../components/Modal.tsx';
import { SignupBrandDialog } from '../components/SignupBrandDialog.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { ownerIdentityLabel } from '../lib/ownerLabel.ts';
import {
  buildOpsUserPickerOptions,
  duplicateDisplayNameIds,
  opsUserPickerIdentityFromRecord,
} from '../lib/opsUserPickerLabel.ts';
import {
  Button,
  Chip,
  EmptyState,
  IconButton,
  Input,
  KpiCard,
  PageHeader,
  Select,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
} from '../ui/index.ts';
import type { SelectOption } from '../ui/index.ts';

const PAGE_LIMIT = 50;

/**
 * Radix Select forbids empty-string option values, so the "unset"
 * choice travels as this sentinel and is translated back to `''` in
 * the change handler — state semantics stay identical to the old
 * native `<option value="">` placeholder row.
 */
const UNSET_MANAGER_SENTINEL = 'none';

interface Scope {
  readonly email: string;
  readonly offset: number;
  /** Admin-only cross-brand filter ('' = all brands, #1040). */
  readonly brand: string;
}

function truncateId(value: string, head = 8, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function ownerLabel(
  row: CustomerManagerCustomerSummary,
  translate: (key: I18nKey) => string,
): string {
  return ownerIdentityLabel(
    {
      display_name: row.customer_manager_display_name,
      email: row.customer_manager_email,
      ops_user_id: row.customer_manager_ops_user_id,
    },
    row.customer_manager_login_methods,
    translate,
    '',
  );
}

function opsUserLabel(user: OpsUserRecord): string {
  const displayName = user.display_name.trim();
  if (displayName.length > 0 && displayName !== user.email) {
    return `${displayName} · ${user.email}`;
  }
  return user.email;
}

function hasCustomerManagerRole(user: OpsUserRecord): boolean {
  return user.roles.includes(ROLE_OPS_CUSTOMER_MANAGER);
}

export function CustomerOwnershipPage(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const roles = user?.roles ?? [];
  const isAdmin = hasAnyOpsRole(roles, [ROLE_OPS_ADMIN]);
  const [draftEmail, setDraftEmail] = useState('');
  const [scope, setScope] = useState<Scope>({ email: '', offset: 0, brand: '' });
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerManagerCustomerSummary | null>(null);
  const [brandEditTarget, setBrandEditTarget] =
    useState<CustomerManagerCustomerSummary | null>(null);
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const query = useCustomerOwnershipCustomers({
    limit: PAGE_LIMIT,
    offset: scope.offset,
    email: scope.email,
    brand: scope.brand.length > 0 ? scope.brand : undefined,
  });

  const rows = useMemo(() => query.data?.customers ?? [], [query.data]);
  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? scope.offset;
  const currentLimit = query.data?.limit ?? PAGE_LIMIT;
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + currentLimit < total;
  const assignedOnPage = rows.filter(
    (row) => row.customer_manager_ops_user_id !== null,
  ).length;
  const unassignedOnPage = rows.length - assignedOnPage;

  function handleSearch(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setScope((prev) => ({ ...prev, email: draftEmail.trim(), offset: 0 }));
  }

  function handleClear(): void {
    setDraftEmail('');
    setScope((prev) => ({ ...prev, email: '', offset: 0 }));
  }

  return (
    <section data-testid="customer-ownership-page" className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-label text-signal">{t('customer_ownership.kicker')}</span>
          <PageHeader
            title={
              isAdmin
                ? t('customer_ownership.admin.title')
                : t('customer_ownership.manager.title')
            }
            sub={
              isAdmin
                ? t('customer_ownership.admin.subtitle')
                : t('customer_ownership.manager.subtitle')
            }
          />
          <div className="mt-2">
            <BrandScopeBanner
              testIdPrefix="customer-ownership"
              brandFilter={scope.brand}
              onBrandFilterChange={(brand) =>
                setScope((prev) => ({ ...prev, brand, offset: 0 }))
              }
            />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <KpiCard
            label={t('customer_ownership.metric.total')}
            value={total.toLocaleString('en-US')}
            testId="customer-ownership-total"
          />
          <KpiCard
            label={t('customer_ownership.metric.assigned_page')}
            value={assignedOnPage.toLocaleString('en-US')}
            testId="customer-ownership-assigned-page"
          />
          <KpiCard
            label={t('customer_ownership.metric.unassigned_page')}
            value={unassignedOnPage.toLocaleString('en-US')}
            testId="customer-ownership-unassigned-page"
          />
        </div>
      </div>

      {!isAdmin ? (
        <div
          data-testid="customer-ownership-readonly"
          className="flex items-center gap-3 rounded-lg border border-line-soft bg-card px-4 py-3 text-body text-paper-mid"
        >
          <Lock aria-hidden="true" className="h-4 w-4 shrink-0 text-signal" />
          <span>{t('customer_ownership.manager.readonly')}</span>
        </div>
      ) : null}

      <form
        className="flex flex-wrap items-center gap-3 rounded-lg border border-line-soft bg-card p-4 max-md:p-3"
        onSubmit={handleSearch}
        data-testid="customer-ownership-search-form"
        noValidate
      >
        <div className="relative min-w-72 flex-1 max-md:min-w-0 max-md:basis-full">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-paper-low"
          />
          <Input
            type="search"
            value={draftEmail}
            onChange={(e) => setDraftEmail(e.currentTarget.value)}
            placeholder={t('customer_ownership.search.placeholder')}
            aria-label={t('customer_ownership.search.aria_label')}
            data-testid="customer-ownership-search-input"
            className="pl-9"
          />
        </div>
        <Button type="submit" variant="primary" data-testid="customer-ownership-search-submit" className="max-md:flex-1">
          <Filter aria-hidden="true" className="h-3.5 w-3.5" />
          <span>{t('customer_ownership.search.submit')}</span>
        </Button>
        {scope.email.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            onClick={handleClear}
            data-testid="customer-ownership-search-clear"
            className="max-md:flex-1"
          >
            {t('customer_ownership.search.clear')}
          </Button>
        ) : null}
        <Button
          type="button"
          onClick={() => void query.refetch()}
          data-testid="customer-ownership-refresh"
        >
          <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
          <span>{t('common.refresh')}</span>
        </Button>
      </form>

      {query.isError ? (
        <div
          role="alert"
          data-testid="customer-ownership-error"
          className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3"
        >
          <div className="flex flex-col gap-1">
            <span className="text-body font-semibold text-bad">
              {t('customer_ownership.error.title')}
            </span>
            <span className="text-dense text-bad-bright">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => void query.refetch()}
            data-testid="customer-ownership-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {query.isPending ? (
        <div
          data-testid="customer-ownership-loading"
          className="flex flex-col gap-2 rounded-lg border border-line-soft bg-card p-4"
        >
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12" />
          ))}
          <span className="sr-only">{t('customer_ownership.loading_aria')}</span>
        </div>
      ) : null}

      {query.isSuccess && rows.length === 0 ? (
        <EmptyState
          testId="customer-ownership-empty"
          className="rounded-lg border border-line-soft bg-card"
          title={
            scope.email.length > 0
              ? t('customer_ownership.empty.filtered.title')
              : isAdmin
                ? t('customer_ownership.empty.admin.title')
                : t('customer_ownership.empty.manager.title')
          }
          hint={
            scope.email.length > 0
              ? t('customer_ownership.empty.filtered.body')
              : isAdmin
                ? t('customer_ownership.empty.admin.body')
                : t('customer_ownership.empty.manager.body')
          }
        />
      ) : null}

      {query.isSuccess && rows.length > 0 ? (
        <>
          <Table testId="customer-ownership-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('customer_ownership.column.customer')}</Th>
                <Th>{t('customer_ownership.column.org')}</Th>
                <Th num>{t('customer_ownership.column.balance')}</Th>
                <Th>{t('customer_ownership.column.brand')}</Th>
                <Th>{t('customer_ownership.column.owner')}</Th>
                <Th>{t('customer_ownership.column.assigned_at')}</Th>
                <Th>{t('customer_ownership.column.created')}</Th>
                <Th className="text-right">{t('customer_ownership.column.actions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((row) => (
                <CustomerOwnershipRow
                  key={row.account_id}
                  row={row}
                  isAdmin={isAdmin}
                  cnyPerUsd={cnyPerUsd}
                  onOpenAssignment={setSelectedCustomer}
                  onEditBrand={setBrandEditTarget}
                />
              ))}
            </TBody>
          </Table>
          <nav
            aria-label={t('customer_ownership.pagination.aria_label')}
            data-testid="customer-ownership-pagination"
            className="flex items-center justify-between gap-4 rounded-lg border border-line-soft bg-card px-4 py-3"
          >
            <span className="text-dense text-paper-low">
              {t('customer_ownership.pagination.range', {
                start: currentOffset + 1,
                end: Math.min(currentOffset + rows.length, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!hasPrev || query.isFetching}
                onClick={() =>
                  setScope((prev) => ({
                    ...prev,
                    offset: Math.max(0, prev.offset - PAGE_LIMIT),
                  }))
                }
                data-testid="customer-ownership-pagination-prev"
              >
                {t('common.previous')}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!hasNext || query.isFetching}
                onClick={() =>
                  setScope((prev) => ({ ...prev, offset: prev.offset + PAGE_LIMIT }))
                }
                data-testid="customer-ownership-pagination-next"
              >
                {t('common.next')}
              </Button>
            </div>
          </nav>
        </>
      ) : null}

      {selectedCustomer !== null && isAdmin ? (
        <AssignmentDrawer
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      ) : null}

      {brandEditTarget !== null && isAdmin ? (
        <SignupBrandDialog
          accountId={brandEditTarget.account_id}
          email={brandEditTarget.email}
          currentBrand={brandEditTarget.signup_brand}
          onClose={() => setBrandEditTarget(null)}
        />
      ) : null}
    </section>
  );
}

function CustomerOwnershipRow({
  row,
  isAdmin,
  cnyPerUsd,
  onOpenAssignment,
  onEditBrand,
}: {
  readonly row: CustomerManagerCustomerSummary;
  readonly isAdmin: boolean;
  readonly cnyPerUsd: number | null;
  readonly onOpenAssignment: (row: CustomerManagerCustomerSummary) => void;
  readonly onEditBrand: (row: CustomerManagerCustomerSummary) => void;
}): JSX.Element {
  const t = useT();
  const assigned = row.customer_manager_ops_user_id !== null;
  return (
    <Tr data-testid={`customer-ownership-row-${row.account_id}`}>
      <Td>
        <CustomerCell
          orgId={row.org_id}
          secondary={
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-body font-medium text-paper">{row.email}</span>
              <span className="truncate text-dense text-paper-low">{row.org_name}</span>
            </div>
          }
        />
      </Td>
      <Td mono title={row.org_id}>
        {truncateId(row.org_id)}
      </Td>
      <Td num emphasis data-testid={`customer-ownership-row-${row.account_id}-balance`}>
        {formatUsdMicrosOrCentsAsCnyDisplayBalance(row.balance_usd_micros, row.balance_usd_cents, cnyPerUsd)}
      </Td>
      <Td>
        <div className="flex items-center gap-1.5">
          <BrandChip
            brand={row.signup_brand}
            testId={`customer-ownership-row-${row.account_id}-brand`}
          />
          {isAdmin ? (
            <IconButton
              label={t('brand_edit.action_aria')}
              onClick={() => onEditBrand(row)}
              data-testid={`customer-ownership-row-${row.account_id}-edit-brand`}
            >
              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
          ) : null}
        </div>
      </Td>
      <Td>
        <div className="flex min-w-0 flex-col items-start gap-1">
          {assigned ? (
            <>
              <Chip
                tone="ok"
                testId={`customer-ownership-row-${row.account_id}-owner-status`}
              >
                {t('customer_ownership.owner.assigned')}
              </Chip>
              <span
                className="max-w-full truncate text-dense text-paper"
                data-testid={`customer-ownership-row-${row.account_id}-owner`}
              >
                {ownerLabel(row, t)}
              </span>
            </>
          ) : (
            <Chip
              tone="neutral"
              testId={`customer-ownership-row-${row.account_id}-owner-status`}
            >
              {t('customer_ownership.owner.unassigned')}
            </Chip>
          )}
        </div>
      </Td>
      <Td mono>{formatLocalTimestamp(row.assigned_at)}</Td>
      <Td mono>{formatLocalTimestamp(row.created_at, { seconds: true })}</Td>
      <Td className="text-right">
        {isAdmin ? (
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => onOpenAssignment(row)}
              data-testid={`customer-ownership-row-${row.account_id}-assign`}
            >
              <UserCheck aria-hidden="true" className="h-3.5 w-3.5" />
              <span>
                {assigned
                  ? t('customer_ownership.action.reassign')
                  : t('customer_ownership.action.assign')}
              </span>
            </Button>
          </div>
        ) : (
          <Chip
            tone="neutral"
            lamp={false}
            testId={`customer-ownership-row-${row.account_id}-readonly`}
          >
            {t('customer_ownership.action.readonly')}
          </Chip>
        )}
      </Td>
    </Tr>
  );
}

function AssignmentDrawer({
  customer,
  onClose,
}: {
  readonly customer: CustomerManagerCustomerSummary;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const opsUsers = useOpsUsers();
  const assign = useAssignCustomerManager();
  const revoke = useRevokeCustomerManager();
  const [selectedManagerId, setSelectedManagerId] = useState(
    customer.customer_manager_ops_user_id ?? '',
  );

  useEffect(() => {
    setSelectedManagerId(customer.customer_manager_ops_user_id ?? '');
  }, [customer]);

  const assignableManagers = useMemo(
    () =>
      (opsUsers.data?.users ?? [])
        .filter((u) => u.disabled_at === null && hasCustomerManagerRole(u))
        .sort((a, b) => opsUserLabel(a).localeCompare(opsUserLabel(b))),
    [opsUsers.data],
  );
  const managerIdentities = useMemo(
    () => assignableManagers.map(opsUserPickerIdentityFromRecord),
    [assignableManagers],
  );
  const duplicateIds = useMemo(
    () => duplicateDisplayNameIds(managerIdentities),
    [managerIdentities],
  );
  const selectedDuplicate = managerIdentities.find(
    (identity) => identity.id === selectedManagerId && duplicateIds.has(identity.id),
  );
  const selectedDuplicateName = selectedDuplicate?.displayName?.trim() ?? '';
  const currentOwnerId = customer.customer_manager_ops_user_id;
  const selectedIsCurrent = currentOwnerId !== null && selectedManagerId === currentOwnerId;
  const currentOwnerMissing =
    currentOwnerId !== null &&
    !assignableManagers.some((manager) => manager.id === currentOwnerId);
  const isMutating = assign.isPending || revoke.isPending;
  const mutationError = assign.error ?? revoke.error;

  const managerOptions = useMemo<readonly SelectOption[]>(() => {
    const options: SelectOption[] = [
      {
        value: UNSET_MANAGER_SENTINEL,
        label: t('customer_ownership.assignment.select_placeholder'),
      },
    ];
    if (currentOwnerMissing && currentOwnerId !== null) {
      options.push({
        value: currentOwnerId,
        label: t('customer_ownership.assignment.current_out_of_band', {
          owner: ownerLabel(customer, t),
        }),
      });
    }
    for (const option of buildOpsUserPickerOptions(managerIdentities, t)) {
      options.push(option);
    }
    return options;
  }, [currentOwnerId, currentOwnerMissing, customer, managerIdentities, t]);

  async function handleAssign(): Promise<void> {
    if (selectedManagerId.length === 0 || selectedIsCurrent) {
      return;
    }
    try {
      await assign.mutateAsync({
        orgId: customer.org_id,
        customerManagerOpsUserId: selectedManagerId,
      });
      toast.success(t('customer_ownership.toast.assigned'));
      onClose();
    } catch {
      // Inline mutation state renders the error.
    }
  }

  async function handleRevoke(): Promise<void> {
    if (customer.customer_manager_ops_user_id === null) {
      return;
    }
    try {
      await revoke.mutateAsync(customer.org_id);
      toast.success(t('customer_ownership.toast.revoked'));
      onClose();
    } catch {
      // Inline mutation state renders the error.
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="customer-ownership-assignment-title"
      testId="customer-ownership-assignment-drawer"
      containerClassName="items-stretch justify-end"
      panelClassName="h-full max-w-xl rounded-none px-6 py-6"
    >
      <div className="flex h-full flex-col gap-6">
        <header className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-label text-signal">
              {t('customer_ownership.assignment.kicker')}
            </span>
            <h3
              id="customer-ownership-assignment-title"
              className="text-title text-paper"
            >
              {t('customer_ownership.assignment.title')}
            </h3>
          </div>
          <IconButton label={t('common.close')} onClick={onClose} className="shrink-0">
            <X aria-hidden="true" className="h-4 w-4" />
          </IconButton>
        </header>

        <div className="flex flex-col gap-3 rounded-lg border border-line-soft bg-card p-4">
          <span className="text-label text-paper-low">
            {t('customer_ownership.assignment.customer')}
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <CustomerCompanyLine orgId={customer.org_id} className="max-w-full" />
            <span className="truncate text-body font-medium text-paper">
              {customer.email}
            </span>
            <span className="truncate text-dense text-paper-mid">
              {customer.org_name}
            </span>
            <span className="font-mono text-dense text-paper-low">{customer.org_id}</span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-label text-paper-mid">
            {t('customer_ownership.assignment.manager')}
          </span>
          <Select
            value={
              selectedManagerId.length > 0 ? selectedManagerId : UNSET_MANAGER_SENTINEL
            }
            onValueChange={(value) =>
              setSelectedManagerId(value === UNSET_MANAGER_SENTINEL ? '' : value)
            }
            options={managerOptions}
            ariaLabel={t('customer_ownership.assignment.manager')}
            disabled={opsUsers.isPending || isMutating}
            testId="customer-ownership-assignment-select"
          />
          {selectedDuplicate !== undefined && selectedDuplicateName.length > 0 ? (
            <p
              className="m-0 text-dense text-warn"
              data-testid="customer-ownership-assignment-duplicate-warning"
            >
              {t('customer_ownership.assignment.duplicate_name_warning', {
                name: selectedDuplicateName,
              })}
            </p>
          ) : null}
        </div>

        {opsUsers.isError ? (
          <div
            role="alert"
            data-testid="customer-ownership-assignment-users-error"
            className="rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 text-dense text-bad-bright"
          >
            {coerceErrorMessage(opsUsers.error, t('common.network_error'))}
          </div>
        ) : null}

        {mutationError !== null ? (
          <div
            role="alert"
            data-testid="customer-ownership-assignment-error"
            className="rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 text-dense text-bad-bright"
          >
            {coerceErrorMessage(mutationError, t('common.network_error'))}
          </div>
        ) : null}

        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-line-soft pt-4">
          <Button
            type="button"
            variant="danger"
            onClick={() => void handleRevoke()}
            disabled={customer.customer_manager_ops_user_id === null || isMutating}
            data-testid="customer-ownership-assignment-revoke"
          >
            <Unlink aria-hidden="true" className="h-3.5 w-3.5" />
            <span>{t('customer_ownership.action.revoke')}</span>
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" onClick={onClose} disabled={isMutating}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleAssign()}
              loading={isMutating}
              disabled={
                selectedManagerId.length === 0 ||
                selectedIsCurrent ||
                opsUsers.isPending ||
                isMutating
              }
              data-testid="customer-ownership-assignment-save"
            >
              {isMutating ? null : <Save aria-hidden="true" className="h-3.5 w-3.5" />}
              <span>{isMutating ? t('common.saving') : t('common.save')}</span>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
